import Button from 'Components/Base/Button'
import Icon from 'Components/Base/Icon'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import useAnalytics from 'hooks/useAnalytics'
import useDocumentTitle from 'hooks/useDocumentTitle'
import useMessagingCenter from 'hooks/useMessagingCenter'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { getAccessToken } from 'reducers/authSlice'
import { getProfile } from 'reducers/profileSlice'
import { setLoading } from 'reducers/tempSlice'
import styled, { useTheme } from 'styled-components'
import { Category, HelpText, Message, MessageType } from 'types/MessagingCenter'
import CurrentMessage from './CurrentMessage'
import FilterMenu from './FilterMenu'
import MessageRow from './MessageRow'
import NewMessage from './NewMessage'
import ScheduleAFollowUp from './ScheduleAFollowUp'

interface Props {
  isMobile: boolean
  sideMenuToggled: () => void
  messageCategories: Category[]
  setMessageCategories: (cat: Category[]) => void
  newMessageBool: boolean
  setNewMessageBool: (boolean) => void
  currentTypes: MessageType[]
  setCurrentTypes: (types: MessageType[]) => void

  currentCategory: Category | undefined
  setCurrentCategory: (cat: Category | undefined) => void
  currentHelpText: HelpText[]
  setCurrentHelpText: (helpText: HelpText[]) => void
  currentType: MessageType | undefined
  setCurrentType: (type: MessageType) => void
  typeValue: number
  setTypeValue: (type: number) => void
  categoryValue: number | undefined
  setCategoryValue: (type: number) => void
  setReplyToTriaMessageId: (number: number | undefined) => void
  replyToTriaMessageId: number | undefined
}

export default function MessagingInboxScreen({
  isMobile,
  sideMenuToggled,

  messageCategories,
  newMessageBool,
  setNewMessageBool,
  currentTypes,
  setCurrentTypes,
  currentCategory,
  setCurrentCategory,
  currentHelpText,
  setCurrentHelpText,
  setMessageCategories,
  currentType,
  setCurrentType,
  typeValue,
  setTypeValue,
  categoryValue,
  setCategoryValue,
  replyToTriaMessageId,
  setReplyToTriaMessageId,
}: Props) {
  const [patientMessages, setPatientMessages] = useState<Message[]>([])
  const [archiveMessages, setArchivedMessages] = useState<Message[]>([])
  const [currentMessage, setCurrentMessage] = useState<Message>(patientMessages[0])
  const [nextMessage, setNextMessage] = useState<Message>(patientMessages[1])
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([])
  const [filteredMessagesReversed, setFilteredMessagesReversed] = useState<Message[]>([])

  const [searchParams, setSearchParams] = useSearchParams()

  const [engagedBool, setEngagedBool] = useState<boolean>(true)
  const [afterSend, setAfterSend] = useState<boolean>(false)
  const [replyBool, setReplyBool] = useState<boolean>(false)
  const [refreshMessagesBool, setRefreshMessagesBool] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [attachmentDropOpen, setAttachmentDropOpen] = useState(true)
  const [postErrorBool, setPostErrorBool] = useState<boolean>(false)
  const [dropFilter, setDropFilter] = useState<number>(1)
  const [chrono, setChrono] = useState<boolean>(true)
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const [showArchive, setShowArchive] = useState<boolean>(false)

  const [defaultCategory, setDefaultCategory] = useState<number>()
  const [defaultType, setDefaultType] = useState<number>()

  const navigate = useNavigate()
  const profile = useAppSelector(getProfile)

  const { setDocumentTitle } = useDocumentTitle()
  setDocumentTitle('Messaging Inbox')

  const dispatch = useAppDispatch()
  const accessToken = useAppSelector(getAccessToken)
  const { viewPatientMessage, archivePatientMessage, getPatientMessagingCategories, getMessages, updateTriaMessage, viewTriaMessage } =
    useMessagingCenter(accessToken!)
  const theme = useTheme()

  const token = useAppSelector(getAccessToken)
  const analytics = useAnalytics(token!)

  useEffect(() => {
    //dispatch(setLoading(true))

    getPatientMessagingCategories().then((res) => {
      setMessageCategories(res.data.categories)
    })
  }, [])

  useEffect(() => {
    if (searchParams.get('newMessage') === 'true') {
      setDefaultCategory(4)
      setDefaultType(0)
      handleNewMessageClick()
    }
  }, [])

  useEffect(() => {
    if (chrono) {
      setCurrentMessage(filteredMessages[0])
    } else {
      setCurrentMessage(filteredMessagesReversed[0])
    }
  }, [chrono])

  useEffect(() => {
    dispatch(setLoading(true))
    if (profile?.enrollmentStatus === 'IN') {
      setEngagedBool(false)
    }
    getMessages()
      .then((res) => {
        setCurrentMessage(res.data.messages[0])
        setNextMessage(res.data.messages[1])
        setPatientMessages(res.data.messages)
        setArchivedMessages(res.data.archivedMessages)
        setFilteredMessages(res.data.messages)
        setFilteredMessagesReversed(res.data.messages.slice().reverse())
      })
      .finally(() => dispatch(setLoading(false)))
  }, [])

  useEffect(() => {
    if (!showArchive) {
      if (dropFilter === 1) {
        setFilteredMessages(patientMessages ?? [])
        setCurrentMessage(patientMessages[0] ?? [])
        setFilteredMessagesReversed(patientMessages.slice().reverse() ?? [])
      } else if (dropFilter === 2) {
        setFilteredMessages(
          patientMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
          ) ?? [],
        )
        setFilteredMessagesReversed(
          patientMessages
            .slice()
            .filter(
              (row) =>
                row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
            )
            .reverse() ?? [],
        )
        setCurrentMessage(
          patientMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
          )[0] ?? [],
        )
      } else if (dropFilter === 3) {
        setFilteredMessages(
          patientMessages.filter(
            (row) =>
              (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
              (row.conversations &&
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
          ) ?? [],
        )
        setFilteredMessagesReversed(
          patientMessages
            .slice()
            .filter(
              (row) =>
                (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                (row.conversations &&
                  row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
            )
            .reverse() ?? [],
        )
        setCurrentMessage(
          patientMessages.filter(
            (row) =>
              (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
              (row.conversations &&
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
          )[0] ?? [],
        )
      } else if (dropFilter === 4) {
        setFilteredMessages(
          patientMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
          ) ?? [],
        )
        setFilteredMessagesReversed(
          patientMessages
            .slice()
            .filter(
              (row) =>
                row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
            )
            .reverse() ?? [],
        )
        setCurrentMessage(
          patientMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
          )[0] ?? [],
        )
      }
    } else {
      if (dropFilter === 1) {
        setFilteredMessages(archiveMessages ?? [])
        setFilteredMessagesReversed(archiveMessages.slice().reverse() ?? [])
        setCurrentMessage(archiveMessages[0] ?? [])
      } else if (dropFilter === 2) {
        setFilteredMessages(
          archiveMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
          ) ?? [],
        )
        setFilteredMessagesReversed(
          archiveMessages
            .slice()
            .filter(
              (row) =>
                row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
            )
            .reverse() ?? [],
        )
        setCurrentMessage(
          archiveMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
          )[0] ?? [],
        )
      } else if (dropFilter === 3) {
        setFilteredMessages(
          archiveMessages.filter(
            (row) =>
              (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
              (row.conversations &&
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
          ) ?? [],
        )
        setFilteredMessagesReversed(
          archiveMessages
            .slice()
            .filter(
              (row) =>
                (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                (row.conversations &&
                  row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
            )
            .reverse() ?? [],
        )
        setCurrentMessage(
          archiveMessages.filter(
            (row) =>
              (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
              (row.conversations &&
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
          )[0] ?? [],
        )
      } else if (dropFilter === 4) {
        setFilteredMessages(
          archiveMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
          ) ?? [],
        )
        setFilteredMessagesReversed(
          archiveMessages
            .slice()
            .filter(
              (row) =>
                row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
                row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
            )
            .reverse() ?? [],
        )
        setCurrentMessage(
          archiveMessages.filter(
            (row) =>
              row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
              row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
          )[0] ?? [],
        )
      }
    }
  }, [dropFilter])

  useEffect(() => {
    if (showArchive) {
      setFilteredMessages(archiveMessages ?? [])
      setFilteredMessagesReversed(archiveMessages.slice().reverse() ?? [])
      setCurrentMessage(archiveMessages[0] ?? [])
      setDropFilter(1)
    } else {
      setFilteredMessages(patientMessages ?? [])
      setCurrentMessage(patientMessages[0] ?? [])
      setFilteredMessagesReversed(patientMessages.slice().reverse() ?? [])
      setDropFilter(1)
    }
  }, [showArchive])

  const selectMessage = (message: Message) => {
    setAfterSend(false)
    setReplyBool(false)
    setNewMessageBool(false)
    setCurrentMessage(message)
    setNextMessage(filteredMessages[filteredMessages.findIndex((m) => m.patientInitiatedMessagePId === message.patientInitiatedMessagePId) + 1])
    setPostErrorBool(false)
    if (message.hasNewMessage) {
      if (message.patientInitiatedMessagePId) {
        message.conversations.map((history) =>
          viewPatientMessage(history.patientInitiatedMessageHistoryPId)
            .then(() => {
              getMessages().then((res) => {
                refreshMessagesUponView(res)
              })
            })
            .finally(() => scrollDown()),
        )
      }
      if (message.triaInitiatedMessagePId) {
        if (!message.viewedDate) {
          viewTriaMessage(message.triaInitiatedMessagePId)
            .then(() => {
              getMessages().then((res) => {
                refreshMessagesUponView(res)
              })
            })
            .finally(() => scrollDown())
        }
      }
    }

    if (message.hasNewMessage) {
      message.conversations.map((history) =>
        viewPatientMessage(history.patientInitiatedMessageHistoryPId)
          .then(() => {
            getMessages().then((res) => {
              refreshMessagesUponView(res)
            })
          })
          .finally(() => scrollDown()),
      )
    }
    navigate('')
  }
  const refreshMessagesUponView = (res) => {
    setPatientMessages(res.data.messages)
    if (searchValue === '') {
      setFilteredMessages(res.data.messages)
      setFilteredMessagesReversed(res.data.messages.slice().reverse())
    } else {
      setFilteredMessages(res.data.messages)
      setFilteredMessagesReversed(res.data.messages.slice().reverse())
    }
  }

  const filterMessage = (search) => {
    setSearchValue(search)
    if (search === '') {
      showArchive ? setFilteredMessages(archiveMessages) : setFilteredMessages(patientMessages)
    } else {
      showArchive
        ? setFilteredMessages(
            archiveMessages.filter(
              (m) =>
                (m.messageType && m.messageType.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
                (m.subject && m.subject.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
                m.conversations.filter((c) => c.body.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())).length > 0 ||
                m.conversations.filter(
                  (c) =>
                    c.patientAttachments.filter((a) => a.displayName && a.displayName.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()))
                      .length > 0,
                ).length > 0 ||
                m.triaAttachments.filter((a) => a.displayName && a.displayName.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())).length >
                  0,
            ),
          )
        : setFilteredMessages(
            patientMessages.filter(
              (m) =>
                (m.messageType && m.messageType.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
                (m.subject && m.subject.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
                m.conversations.filter((c) => c.body.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())).length > 0 ||
                m.conversations.filter(
                  (c) =>
                    c.patientAttachments.filter((a) => a.displayName && a.displayName.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()))
                      .length > 0,
                ).length > 0 ||
                m.triaAttachments.filter((a) => a.displayName && a.displayName.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())).length >
                  0,
            ),
          )
    }
  }

  const handleNewMessageClick = () => {
    setReplyToTriaMessageId(undefined)
    setNewMessageBool(!newMessageBool)
    setPostErrorBool(false)
    if (!newMessageBool) {
      analytics.postAnalyticsPageView(`/messaging/inbox/newMessage`)
    }
    setSearchValue('')
    setFilteredMessages(patientMessages)
    setCategoryValue(-1)
    setCurrentHelpText([])
    setReplyBool(false)
  }

  const refreshMessagesUponSend = (archiveReplyOrSend) => {
    setPostErrorBool(false)
    dispatch(setLoading(true))
    getMessages()
      .then((res) => {
        setNextMessage(res.data.messages[1])
        setPatientMessages(res.data.messages)
        setArchivedMessages(res.data.archivedMessages)
        setFilteredMessages(showArchive ? res.data.archivedMessages : res.data.messages)
        if (archiveReplyOrSend === 1) {
          if (dropFilter === 3) {
            if (showArchive) {
              setCurrentMessage(
                res.data.archivedMessages
                  .filter(
                    (row) =>
                      (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                      (row.conversations &&
                        row.conversations.find(
                          (m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0,
                        )),
                  )
                  .find((message) =>
                    message.patientInitiatedMessagePId
                      ? message.patientInitiatedMessagePId === currentMessage.patientInitiatedMessagePId
                      : message.triaInitiatedMessagePId === currentMessage.triaInitiatedMessagePId,
                  ) ??
                  res.data.messages.filter(
                    (row) =>
                      (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                      (row.conversations &&
                        row.conversations.find(
                          (m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0,
                        )),
                  )[0],
              )
            } else {
              setCurrentMessage(
                res.data.messages
                  .filter(
                    (row) =>
                      (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                      (row.conversations &&
                        row.conversations.find(
                          (m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0,
                        )),
                  )
                  .find((message) =>
                    message.patientInitiatedMessagePId
                      ? message.patientInitiatedMessagePId === currentMessage.patientInitiatedMessagePId
                      : message.triaInitiatedMessagePId === currentMessage.triaInitiatedMessagePId,
                  ) ??
                  res.data.messages.filter(
                    (row) =>
                      (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                      (row.conversations &&
                        row.conversations.find(
                          (m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0,
                        )),
                  )[0],
              )
            }
          } else {
            setCurrentMessage(
              res.data.messages.find((message) =>
                message.patientInitiatedMessagePId
                  ? message.patientInitiatedMessagePId === currentMessage.patientInitiatedMessagePId
                  : message.triaInitiatedMessagePId === currentMessage.triaInitiatedMessagePId,
              ),
            )
          }

          if (!showArchive) {
            setCurrentMessage(
              res.data.messages.find((message) =>
                message.patientInitiatedMessagePId
                  ? message.patientInitiatedMessagePId === currentMessage.patientInitiatedMessagePId
                  : message.triaInitiatedMessagePId === currentMessage.triaInitiatedMessagePId,
              ),
            )
            if (dropFilter === 1) {
              setFilteredMessages(res.data.messages ?? [])

              setFilteredMessagesReversed(res.data.messages.slice().reverse() ?? [])
            } else if (dropFilter === 2) {
              setFilteredMessages(
                res.data.messages.filter(
                  (row) =>
                    row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
                    row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
                ) ?? [],
              )
              setFilteredMessagesReversed(
                res.data.messages
                  .slice()
                  .filter(
                    (row) =>
                      row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
                      row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
                  )
                  .reverse() ?? [],
              )
            } else if (dropFilter === 3) {
              setFilteredMessages(
                res.data.messages.filter(
                  (row) =>
                    (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                    (row.conversations &&
                      row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
                ) ?? [],
              )
              setFilteredMessagesReversed(
                res.data.messages
                  .slice()
                  .filter(
                    (row) =>
                      (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                      (row.conversations &&
                        row.conversations.find(
                          (m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0,
                        )),
                  )
                  .reverse() ?? [],
              )
            } else if (dropFilter === 4) {
              setFilteredMessages(
                res.data.messages.filter(
                  (row) =>
                    row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
                    row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
                ) ?? [],
              )
              setFilteredMessagesReversed(
                res.data.messages
                  .slice()
                  .filter(
                    (row) =>
                      row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
                      row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
                  )
                  .reverse() ?? [],
              )
            }
          } else {
            setCurrentMessage(
              res.data.archivedMessages.find((message) =>
                message.patientInitiatedMessagePId
                  ? message.patientInitiatedMessagePId === currentMessage.patientInitiatedMessagePId
                  : message.triaInitiatedMessagePId === currentMessage.triaInitiatedMessagePId,
              ),
            )
            if (dropFilter === 1) {
              setFilteredMessages(res.data.archivedMessages ?? [])
              setFilteredMessagesReversed(res.data.archivedMessages.slice().reverse() ?? [])
            } else if (dropFilter === 2) {
              setFilteredMessages(
                res.data.archivedMessages.filter(
                  (row) =>
                    row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
                    row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
                ) ?? [],
              )
              setFilteredMessagesReversed(
                res.data.archivedMessages
                  .slice()
                  .filter(
                    (row) =>
                      row.triaAttachments.filter((a) => a.attachmentType === 1).length > 0 ||
                      row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1).length > 0),
                  )
                  .reverse() ?? [],
              )
            } else if (dropFilter === 3) {
              setFilteredMessages(
                res.data.archivedMessages.filter(
                  (row) =>
                    (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                    (row.conversations &&
                      row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0)),
                ) ?? [],
              )
              setFilteredMessagesReversed(
                res.data.archivedMessages
                  .slice()
                  .filter(
                    (row) =>
                      (row.triaAttachments && row.triaAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0) ||
                      (row.conversations &&
                        row.conversations.find(
                          (m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 1 && a.liked).length > 0,
                        )),
                  )
                  .reverse() ?? [],
              )
            } else if (dropFilter === 4) {
              setFilteredMessages(
                res.data.archivedMessages.filter(
                  (row) =>
                    row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
                    row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
                ) ?? [],
              )
              setFilteredMessagesReversed(
                res.data.archivedMessages
                  .slice()
                  .filter(
                    (row) =>
                      row.triaAttachments.filter((a) => a.attachmentType === 2).length > 0 ||
                      row.conversations.find((m) => m.fromTria && m.patientAttachments.filter((a) => a.attachmentType === 2).length > 0),
                  )
                  .reverse() ?? [],
              )
            }
          }
        } else if (archiveReplyOrSend === 2) {
          setCurrentMessage(res.data.messages[0])
        }
      })
      .finally(() => {
        dispatch(setLoading(false))
        setRefreshMessagesBool(!refreshMessagesBool)
      })
  }

  const handleArchiveClick = (messageId) => {
    if (currentMessage.triaInitiatedMessagePId) {
      updateTriaMessage(currentMessage.triaInitiatedMessagePId)
    }
    archivePatientMessage(messageId)
      //.then(() => refreshMessagesUponSend(0))
      .finally(() => {
        setCurrentMessage(nextMessage)
        if (
          filteredMessages.length !==
          filteredMessages.findIndex((message) => message.patientInitiatedMessagePId === currentMessage.patientInitiatedMessagePId) + 1
        ) {
          // selectMessage(
          //   filteredMessages[filteredMessages.findIndex((message) => message.patientInitiatedMessagePId === nextMessage.patientInitiatedMessagePId)],
          // )
          getMessages()
            .then((res) => {
              setPatientMessages(res.data.messages)
              setArchivedMessages(res.data.archivedMessages)
              setFilteredMessages(res.data.messages)
            })
            .then(() => {
              setSearchValue('')
            })
        } else {
          getMessages()
            .then((res) => {
              setNextMessage(res.data.messages[1])
              setPatientMessages(res.data.messages)
              setArchivedMessages(res.data.archivedMessages)
              setFilteredMessages(res.data.messages)
              setCurrentMessage(res.data.messages[0])
              selectMessage(res.data.messages[0])
            })
            .then(() => {
              setSearchValue('')
            })
        }
      })
  }

  useCallback(() => {
    scrollDown()
  }, [replyBool])

  const conversation = useRef<null | HTMLDivElement>(null)

  const scrollDown = () => {
    conversation.current && (conversation.current.scrollTop = conversation.current.scrollHeight)
  }

  return (
    <>
      <Row>
        {!isMobile && (
          <Button
            text="New Message"
            backgroundColor={theme.colors.charts.inRange}
            rightIcon="plus"
            onClick={() => handleNewMessageClick()}
            styles={{ marginLeft: 'auto', marginBottom: -40 }}
          />
        )}
        {isMobile && <div></div>}
      </Row>
      <StyledCardContainer>
        <StyledMessageList>
          <Row>
            <Typography styles={{ marginLeft: 18, marginTop: 10, marginBottom: 10, fontWeight: 600 }} size={18}>
              Messages
            </Typography>
            <StyledDropdown
              style={showArchive ? { width: 85 } : { width: 70 }}
              onChange={() => {
                setShowArchive(!showArchive)
                setDropFilter(1)
              }}
              value={showArchive ? 2 : 1}
            >
              <option value={1} style={{ fontSize: 18 }}>
                Inbox
              </option>

              <option value={2} style={{ fontSize: 18 }}>
                Archive
              </option>
            </StyledDropdown>
          </Row>
          <Row>
            <StyledSearchBar>
              <input
                value={searchValue}
                onChange={(e) => filterMessage(e.target.value)}
                placeholder="Search"
                style={{ width: '100%', height: 48, border: '1px solid #E0E0E0', marginLeft: 20 }}
              />
              <Icon name={'search'} color={'#828282'} styles={{ marginLeft: -30, marginTop: 14 }} />
            </StyledSearchBar>
            <div
              style={{ border: '2px solid #e0e0e0', borderRadius: 6, height: 48, width: 48, marginLeft: 30, cursor: 'pointer' }}
              onClick={() => {
                setFilterOpen(!filterOpen)
              }}
            >
              <Icon name={'filter-list'} styles={{ marginLeft: 10, marginTop: 10 }} color={theme.colors.carePlan.history} />
            </div>

            <div
              style={{ border: '2px solid #e0e0e0', borderRadius: 6, height: 48, width: 48, marginLeft: 10, cursor: 'pointer' }}
              onClick={() => {
                setChrono(!chrono)
              }}
            >
              <Icon name={chrono ? 'sort-down' : 'sort-up'} styles={{ marginLeft: 10, marginTop: 10 }} color={theme.colors.carePlan.history} />
            </div>
            {filterOpen && (
              <FilterMenu
                handleFilterOpen={(filterOpen: boolean) => setFilterOpen(filterOpen)}
                handleSetDropFilter={(df: number) => setDropFilter(df)}
                dropFilter={dropFilter}
              />
            )}
          </Row>
          <MessageRow
            currentMessage={currentMessage}
            messages={chrono ? filteredMessages : filteredMessagesReversed}
            handleMessageSelect={(message: Message) => selectMessage(message)}
          />
        </StyledMessageList>
      </StyledCardContainer>
      {isMobile && (
        <Row styles={{ justifyContent: 'space-between' }}>
          <div></div>
          <Button
            text="New Message"
            backgroundColor={theme.colors.charts.inRange}
            rightIcon="plus"
            onClick={() => handleNewMessageClick()}
            styles={{ marginTop: 85 }}
          />
        </Row>
      )}
      {!newMessageBool && !afterSend && patientMessages.length > 0 ? (
        <CurrentMessage
          isMobile={isMobile}
          currentMessage={currentMessage}
          refreshMessagesUponSend={(num: number) => refreshMessagesUponSend(num)}
          handleArchiveClick={(PIMessageId: number) => handleArchiveClick(PIMessageId)}
          replyBool={replyBool}
          setReplyBool={(bool: boolean) => setReplyBool(bool)}
          setAttachmentDropOpen={(bool: boolean) => setAttachmentDropOpen(bool)}
          postErrorBool={postErrorBool}
          setPostErrorBool={(bool: boolean) => setPostErrorBool(bool)}
        />
      ) : (
        !newMessageBool &&
        !afterSend && (
          <div
            style={
              isMobile
                ? { marginLeft: 'auto', marginRight: 'auto', width: '95%', marginTop: 0, textAlign: 'center' }
                : { marginLeft: 'auto', marginRight: '8%', width: '45%', marginTop: -400, textAlign: 'center' }
            }
          >
            <Typography font="bold" styles={{ marginTop: 20, marginBottom: 20 }}>
              Hello! If you have any questions or concerns about your medications or overall health, you can send a message to Tria Health right here
              on our portal.
            </Typography>
            <Typography font="bold" styles={{ marginTop: 20, marginBottom: 20 }}>
              Our team of healthcare experts is always happy to help and provide personalized guidance to help you achieve your health goals. Don't
              hesitate to reach out to us anytime, we're here to support you!
            </Typography>
          </div>
        )
      )}
      {!engagedBool && afterSend && <ScheduleAFollowUp />}
      {newMessageBool && !afterSend && (
        <NewMessage
          isMobile={isMobile}
          messageCategories={messageCategories.filter((c) => c.messageTypes.find((t) => t.visible))}
          currentType={currentType}
          currentTypes={currentTypes}
          defaultCategory={defaultCategory}
          defaultType={defaultType}
          setCurrentTypes={(types: MessageType[]) => setCurrentTypes(types)}
          currentCategory={currentCategory}
          setCurrentCategory={(category: Category | undefined) => setCurrentCategory(category)}
          currentHelpText={currentHelpText}
          setCurrentHelpText={(helpText: HelpText[]) => setCurrentHelpText(helpText)}
          typeValue={typeValue}
          setTypeValue={(value: number) => setTypeValue(value)}
          categoryValue={categoryValue}
          setCategoryValue={(value: number) => setCategoryValue(value)}
          setCurrentType={(type: MessageType) => setCurrentType(type)}
          setNewMessageBool={(bool: boolean) => setNewMessageBool(bool)}
          refreshMessagesUponSend={(num: number) => refreshMessagesUponSend(num)}
          newMessageBool={newMessageBool}
          setAfterSend={(bool: boolean) => setAfterSend(bool)}
          engagedBool={engagedBool}
          enrollmentStatus={profile?.enrollmentStatus}
          postErrorBool={postErrorBool}
          setPostErrorBool={(bool: boolean) => setPostErrorBool(bool)}
          replyToTriaMessageId={replyToTriaMessageId}
        />
      )}
    </>
  )
}
const StyledCardContainer = styled.div`
  @media only screen and (min-width: 1000px) {
    display: flex;
    flex-direction: row;
  }
`
const StyledSearchBar = styled.div`
  position: relative;
  display: flex;
  @media only screen and (min-width: 1000px) {
    height: 48px;
    width: 310px;
  }

  @media only screen and (max-width: 1000px) {
    height: 40px;
    width: 60%;
    margin: auto;
  }
`
const StyledDropdown = styled.select`
  text-align: left;
  border: none;
  width: auto; /* Allow the width to adjust dynamically */
  min-width: 10px; /* Optional: Set a minimum width */
  max-width: 300px;
  text-indent: 1px;
  text-overflow: '';
  margin-left: 15px;
  margin-right: auto;
  outline: none;
  font-size: 18px;
`
const StyledMessageList = styled.div`
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border: 1px solid #bdbdbd;
  position: relative;

  @media only screen and (min-width: 1000px) {
    height: 823px;
    width: 466px;
  }

  @media only screen and (max-width: 1000px) {
    height: 270px;
    width: 100%;
    margin: auto;
    margin-top: 5%;
  }
`
